import Draggable from "react-draggable";
import clsx from "clsx";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import { Grid, ListItem, Typography } from "@mui/material";
import { AgyleDragIcon } from "../icons/agyle";
import PatientDemographicBanner from "./components/patientDemographicBanner";
import ListItemButton from "@mui/material/ListItemButton";
import List from "@mui/material/List";
import PatientDemographic from "./components/patientPanelComponents/patientDemographic";
import ActivityDemographic from "./components/patientPanelComponents/activityDemographic";
import GpDemographic from "./components/patientPanelComponents/gpDemographic";
import NokDemographic from "./components/patientPanelComponents/nokDemographic";
import SchoolDemographic from "./components/patientPanelComponents/schoolDemographic";
import { useSelector } from "react-redux";
import { ctxPatientAgeType } from "../../store/reducers/patientContext";
import { PATIENT_AGE_TYPE } from "../../util/patientContextUtil";
import PatientBarcode from "./components/patientPanelComponents/patientBarcode";
import API from "../../app/api";
import ReactDOM from "react-dom";

const MODAL_WIDTH = 900;

const useStyles = makeStyles((theme) =>
    ({
        root: {
            position: 'absolute',
            zIndex: 9999,
            backgroundColor: theme.palette.inPatient.formBackground,
            borderRadius: 10,
            boxShadow: `0px 1px 4px 0px rgba(0, 0, 0, 0.25)`,
            width: MODAL_WIDTH,
            maxHeight: 450,
        },
        banner: {
            cursor: 'move',
            padding: '8px 10px',
            borderRadius: '10px 10px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `2px solid ${theme.palette.inPatient.border}`,
        },
        container: {
            minWidth: MODAL_WIDTH,
        },
        sideBanner: {
            borderLeft: `2px solid ${theme.palette.inPatient.border}`,
            padding: 10,
            maxHeight: 350,
        },
        menuItem: {
            display: 'flex',
            flexWrap: 'nowrap',
            flexDirection: 'row',
            alignItems: 'center',
            color: 'grey',
            gap: 10,
            minHeight: 20,
            fontSize: 12,
        },
        menuItemSelected: {
            borderRadius: 6,
            borderRight: '3px solid #2180F7',
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.bookAdmissions.settingMenuItemSelectedText,
            backgroundColor: theme.palette.bookAdmissions.settingMenuItemSelectedBackground,
        },
        panelContainer: {
            display: 'flex',
            gap: 5,
        },
        list: {
            width: 130,
        },
        overlayTopMost: {
            position: 'fixed',
            inset: 0,
            zIndex: 2147483647,
        },
    }),
);

const PANEL_PADDING = 5;
const INIT_Y_POS = 249;

const MENU_ITEM = [
    {id: 'patient', title: 'Patient Details'},
    {id: 'activity', title: 'Activity Details'},
    {id: 'gp', title: 'GP & Surgery'},
    {id: 'nok', title: 'Next of Kin'},
    {id: 'school', title: 'School Details', onlyKid: true},
];

const PatientDemographicModal = ({patient, open, onClose, fromSearch}) => {
    const classes = useStyles();
    const {t} = useTranslation();

    // Selectors
    const patientAgeType = useSelector(ctxPatientAgeType);

    const modalRef = useRef(null);
    const [position, setPosition] = useState({y: INIT_Y_POS, x: window.innerWidth / 2 - (MODAL_WIDTH / 2)});
    const [isDragged, setIsDragged] = useState(true);
    const scrollRef = useRef(null);

    const [gp, setGp] = useState(null);
    const [surgery, setSurgery] = useState(null);
    const [loadingData, setLoadingData] = useState(false);

    // Menu
    const [selectedTab, setSelectedTab] = useState(MENU_ITEM[0].id);

    useEffect(() => {
        if (!open) {
            setPosition({y: INIT_Y_POS, x: window.innerWidth / 2 - (MODAL_WIDTH / 2)});
            // setIsDragged(false);
        }

      if (open && patient) {
        const {gp, surgery} = patient.patient;
        getData(gp, surgery);
      }
    }, [open]);

    // Scroll to the top of the content when the selected tab changes
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({top: 0, behavior: 'smooth'});
        }
    }, [selectedTab]);

    const getData = async (gp, surgery) => {
        setLoadingData(true);
        if (gp) {
            await API.getGpDetails(gp)
                .then(res => {
                    if (res.status === 200) {
                        setGp(res.data);
                    }
                });
        }

        if (surgery) {
            await API.getSurgeryDetails(surgery)
                .then(res => {
                    if (res) {
                        setSurgery(res);
                    }
                });
        }
        setLoadingData(false);
    };

    const handleDrag = (e, data) => {
        const {x, y} = data;

        let newX = x;
        let newY = y;
        if (!fromSearch) {
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;

            newX = Math.max(PANEL_PADDING, Math.min(data.x, windowWidth - (MODAL_WIDTH + 100) - PANEL_PADDING));
            newY = Math.max(PANEL_PADDING + 100, Math.min(data.y, windowHeight - PANEL_PADDING));
        }
        setPosition({x: newX, y: newY});
        if (!isDragged) setIsDragged(true);
    };

    const handleTabChange = (item) => {
        setSelectedTab(item.id);
    };

    const renderContent = () => {
        if (!patient) return null;
        switch (selectedTab) {
            case 'patient':
                return <PatientDemographic patient={patient}/>;
            case 'activity':
                return <ActivityDemographic patient={patient}/>;
            case 'gp':
                return <GpDemographic gp={gp} surgery={surgery} loadingData={loadingData}/>;
            case 'nok':
                return <NokDemographic patient={patient?.patient}/>;
            case 'school':
                if (patientAgeType !== PATIENT_AGE_TYPE.PAEDS) return null;
                return <SchoolDemographic patient={patient?.patient}/>;
            default:
                return null;
        }
    };

    const renderSideBardContent = () => {
        if (!patient) return null;

        switch (selectedTab) {
            case 'activity':
            // return <ActivityDemographic patient={patient}/>;
            case 'patient':
            case 'gp':
            case 'nok':
            case 'school':
            default:
                return <PatientBarcode patient={patient} gp={gp} surgery={surgery} loadingData={loadingData}/>;
        }
    };

    const bound = fromSearch
        ? null
        : {
            left: 0,
            top: 200,
            right: window.innerWidth - (MODAL_WIDTH + 100),
            bottom: window.innerHeight - 300,
        };

    if (!open) return null;

    const modalCore = (
        <Draggable
            axis="both"
            handle=".dialog-header"
            position={fromSearch ? null : position}
            defaultPosition={fromSearch ? {x: window.innerWidth / 2 - (MODAL_WIDTH / 2), y: INIT_Y_POS} : undefined}
            onDrag={handleDrag}
            bounds={bound}
        >
            <div
                className={clsx(classes.root, fromSearch && classes.topMost)}
                ref={modalRef}
            >
                <div className={clsx('dialog-header', classes.banner)}>
                    <b>{t('Patient Demographic Information')}</b>
                    <div style={{display: 'flex', alignItems: 'center', gap: 5}}>
                        <AgyleDragIcon fontSize="small"/>

                        {isDragged &&
                            <IconButton
                                onClick={onClose}
                                size="small"
                            >
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        }
                    </div>
                </div>

                <div className={classes.container}>
                    <Grid container>
                        <Grid item xs={8} style={{padding: 10, maxHeight: 350}}>
                            <PatientDemographicBanner patient={patient}/>
                            <div className={classes.panelContainer}>
                                <List dense className={clsx('scroll-normal-white', classes.list)}>
                                    {MENU_ITEM.map((item) => {
                                        if (fromSearch && item.id === 'activity') return null;
                                        if (item.onlyKid && patientAgeType !== PATIENT_AGE_TYPE.PAEDS) return null;
                                        return (
                                            <ListItem key={item.id} sx={{pl: 0, pr: 1}}>
                                                <ListItemButton
                                                    className={clsx(classes.menuItem, item.id === selectedTab && classes.menuItemSelected)}
                                                    onClick={() => handleTabChange(item)}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight={500}
                                                        fontSize={12}
                                                    >
                                                        {item.title}
                                                    </Typography>
                                                </ListItemButton>
                                            </ListItem>
                                        );
                                    })}
                                </List>

                                <div
                                    className="scroll-normal-white"
                                    style={{width: 'calc(100% - 130px)'}}
                                    ref={scrollRef}
                                >
                                    {renderContent()}
                                </div>
                            </div>
                        </Grid>

                        <Grid item xs={4} className={classes.sideBanner}>
                            <div className="scroll-normal-white">
                                {renderSideBardContent()}
                            </div>
                        </Grid>
                    </Grid>
                </div>
            </div>
        </Draggable>
    );

    if (!fromSearch) {
        return modalCore;
    }

    return ReactDOM.createPortal(
        <div className={classes.overlayTopMost}>
            {modalCore}
        </div>,
        document.body,
    );

};

export default PatientDemographicModal;